// Response/request shapes mirroring the Gravelight backend (A–E). Kept loose where
// the backend returns the full engine state; only the fields the playtest UI reads
// are typed precisely.

export type EligibilityState = 'unactivated' | 'active' | 'frozen';
export type EligibilityClass = 'eligible' | 'under' | 'unverified';
export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'relic';
export type ItemSlotType = 'weapon' | 'armor' | 'trinket' | 'lantern';
export type EquipSlot = 'weapon' | 'armor' | 'trinket1' | 'trinket2' | 'lantern';
export type SealState = 'carried' | 'sealed';
export type CosmeticKind = 'skin' | 'lantern_flame' | 'hub_emote' | 'hood' | 'weapon_style';
export type CosmeticSlot = 'skin' | 'lantern_flame' | 'hub_emote' | 'hood' | 'weapon_style';
export type HubFacing = 'north' | 'south' | 'east' | 'west';
export type SupplyKey = 'oil' | 'torch' | 'potion';

export interface SupplyCounts {
  oil: number;
  torch: number;
  potion: number;
}

export interface SupplyEffects {
  oilFuel: number;
  torchFuel: number;
  potionHeal: number;
}

export interface EffectiveRunStats {
  hpCurrent: number;
  hpMax: number;
  lanternCurrent: number;
  lanternMax: number;
  atk: number;
  def: number;
  burden: number;
  lanternDrainModifier: number;
  oilRestoreBase: number;
  oilRestoreBonus: number;
  oilRestoreTotal: number;
  torchRestoreBase: number;
  torchRestoreBonus: number;
  torchRestoreTotal: number;
  potionHealBase: number;
  potionHealBonus: number;
  potionHealTotal: number;
}

export interface RunSupplyConfig {
  base: SupplyCounts;
  extras: SupplyCounts;
  loaded: SupplyCounts;
  extraCaps: SupplyCounts;
  totalCaps: SupplyCounts;
  /** Backwards-compatible alias for totalCaps. */
  caps: SupplyCounts;
  effects: SupplyEffects;
}

export interface NonceResponse {
  nonce: string;
  message: string;
  expires_at: string;
}

export interface VerifyResponse {
  session: string;
  account_id: string;
  created: boolean;
}

export interface AccountMe {
  account: { id: string; status: string; created_at: string };
  wallet: {
    wallet_address: string;
    bound_at: string;
    last_rebind_at: string | null;
    prev_addresses: string[];
  };
  status: string;
}

export interface EligibilityRefresh {
  state: EligibilityClass;
  eligibility_state: EligibilityState;
  balance?: string;
  activated?: boolean;
}

export interface EligibilityStatus {
  eligibility_state: EligibilityState;
  last_check_result: EligibilityClass | null;
  last_checked_at: string | null;
  cached_at?: string;
  cached_balance?: string;
}

export interface PassStatus {
  balance: number;
  cap: number;
  eligibility_state: EligibilityState;
  frozen: boolean;
  last_regen_at: string | null;
  next_regen_at: string | null;
}

// ── Admin config (Phase L1) ─────────────────────────────────────────────────

export interface AdminConfigRecord {
  key: string;
  value: unknown;
  updatedBy: string | null;
  updatedAt: string | null;
  group: string;
}

export interface AdminConfigResponse {
  config: AdminConfigRecord[];
}

export type AbuseStatus = 'open' | 'reviewed' | 'dismissed' | 'flagged';
export type AbuseSeverity = 'low' | 'medium' | 'high';

export interface AbuseSignalRecord {
  id: number;
  signalType: string;
  accountId: string | null;
  walletCluster: unknown | null;
  severity: AbuseSeverity;
  status: AbuseStatus;
  payload: Record<string, unknown>;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export interface AdminAbuseResponse {
  signals: AbuseSignalRecord[];
}

export interface AdminAbuseScanResponse extends AdminAbuseResponse {
  created: number;
}

export interface RunReplayResult {
  ok: boolean;
  runId: string;
  storedStatus: string;
  replayStatus: string;
  mismatchReasons: string[];
  actionCount: number;
  terminalFloor: number | null;
  extractFloor: number | null;
  settlement?: {
    stored: GoldSettlement | null;
    replayed: GoldSettlement | null;
    matches: boolean;
  };
}

export interface AdminOpsSummary {
  generatedAt: string;
  environment: {
    mode: string;
    verifierModes: {
      cashout: string;
      spinner: string;
    };
  };
  app: { uptimeSeconds: number };
  database: {
    ready: boolean;
    migration: {
      available: boolean;
      latestMigration?: string;
      reason?: string;
    };
  };
  ops: { rewardRunsPaused: boolean };
  rewardRuns: {
    active: number;
    startedLast24h: number;
    terminalLast24h: {
      extracted: number;
      died: number;
      abandoned: number;
      interrupted: number;
    };
  };
  orders: {
    marketplace: { active: number };
    cosmeticMarketplace: { active: number };
    cashout: { active: number };
    spinnerPaid: { pending: number };
  };
  solanaVerification: {
    available: boolean;
    totalLast24h: number | null;
    failuresLast24h: number | null;
    unavailableReason?: string;
  };
  rateLimits: {
    available: boolean;
    hitsByScope: Record<string, number>;
    unavailableReason?: string;
  };
  warnings: string[];
  unavailable: string[];
}

// ── Run engine (subset of the backend ActiveRunState the UI renders) ──────────

export interface Position {
  x: number;
  y: number;
}

export type ItemAffix =
  | { stat: 'attack' | 'defense' | 'max_hp' | 'burden_reduction'; value: number }
  | { stat: 'lantern_efficiency_bps'; value: number }
  | { stat: 'max_lantern' | 'oil_restore_bonus' | 'torch_restore_bonus' | 'potion_heal_bonus'; value: number };

export interface RunFoundItem {
  tempId: string;
  templateId: string;
  rarity: Rarity;
  slotType: ItemSlotType;
  affixes: ItemAffix[];
  durabilityCur: number;
  durabilityMax: number;
  sealDepth: number;
  accountBound: boolean;
  sourceFloor: number;
  sourceKind: 'cache';
  state: 'found' | 'carried' | 'discarded';
}

export interface EnemyState {
  id: string;
  position: Position;
  hp: number;
  damage: number;
}

export interface GoldCacheState {
  id: string;
  position: Position;
  amount: number;
  collected: boolean;
}

export interface ItemDropState {
  item: RunFoundItem;
  position: Position;
}

export interface FloorState {
  floor: number;
  width: number;
  height: number;
  ferryStep: Position | null;
  descentStair: Position;
  enemies: EnemyState[];
  goldCaches: GoldCacheState[];
  itemDrops: ItemDropState[];
}

export interface RunState {
  runId: string;
  accountId: string;
  type: 'reward' | 'echo';
  seed: string;
  status: 'active' | 'died';
  floor: number;
  position: Position;
  hp: number;
  maxHp: number;
  maxLantern?: number;
  attack: number;
  defense: number;
  lanternEfficiencyBps?: number;
  burdenReduction?: number;
  oilRestoreBonus?: number;
  torchRestoreBonus?: number;
  potionHealBonus?: number;
  effectiveStats?: EffectiveRunStats;
  lanternFuel: number;
  supplies?: SupplyCounts;
  config?: {
    supplies?: RunSupplyConfig;
    [key: string]: unknown;
  };
  burden: number;
  carriedGold: number;
  carriedItems: RunFoundItem[];
  foundEquippedSlots: Partial<Record<EquipSlot, string>>;
  turn: number;
  floorState: FloorState;
}

export interface StartRunResult {
  runId: string;
  seed: string;
  state: RunState;
}

/** GET /runs/active — server-side active-run discovery (survives lost local state). */
export interface ActiveRunInfo {
  run_id: string;
  type: string;
  current_floor: number;
  started_at: string | null;
  last_action_at: string | null;
}

export interface ActiveRunStatusResponse {
  active: ActiveRunInfo | null;
}

export interface EngineOutcome {
  kind: string;
  [key: string]: unknown;
}

export interface SupplyUseEvent {
  type: 'supply_used';
  supplyKey: SupplyKey;
  stat: 'lantern' | 'hp';
  configuredAmount: number;
  modifiedAmount?: number;
  actualAppliedAmount: number;
  cappedAmount?: number;
  before: number;
  after: number;
  afterTurn?: number;
  capped: boolean;
  max: number;
}

export type EngineEvent =
  | SupplyUseEvent
  | { type: 'enemy_damaged'; enemyId: string; damage: number; hp: number; killed: boolean; position: Position }
  | { type: 'enemy_moved'; enemyId: string; from: Position; to: Position }
  | { type: 'player_damaged'; source: 'enemy' | 'lantern'; enemyId?: string; damage: number; hp: number; position: Position };

export interface GoldSettlement {
  grossCarriedGold: number;
  toll: number;
  netBankedGold: number;
}

export interface RunItemSummary {
  tempId: string;
  templateId: string;
  rarity: Rarity;
  slotType: ItemSlotType;
  affixes: ItemAffix[];
  durabilityCur: number;
  durabilityMax: number;
  sealDepth: number;
  accountBound: boolean;
  sourceFloor: number;
}

export interface SealedRunItem extends RunItemSummary {
  itemInstanceId: string;
}

export interface LeftBehindItem extends RunItemSummary {
  reason: 'seal_depth' | 'echo_run';
}

export interface ExtractionResult {
  status: 'extracted';
  settlement: GoldSettlement;
  sealed: SealedRunItem[];
  left_behind: LeftBehindItem[];
  message?: string;
}

export interface ActiveActionResult {
  status: 'active';
  state: RunState;
  outcome: EngineOutcome;
  events?: EngineEvent[];
}

export interface DiedActionResult {
  status: 'died';
  outcome: EngineOutcome;
  events?: EngineEvent[];
}

export type ActionResult = ActiveActionResult | DiedActionResult | ExtractionResult;

// ── Merchant supplies and repair ─────────────────────────────────────────────

export interface MerchantRepairableItem {
  itemInstanceId: string;
  rarity: Rarity;
  slotType: ItemSlotType;
  durabilityCur: number;
  durabilityMax: number;
  missingDurability: number;
  repairCost: number;
  equippedSlot: EquipSlot | null;
}

export interface MerchantSalvageItem {
  itemInstanceId: string;
  rarity: Rarity;
  slotType: ItemSlotType;
  affixes: ItemAffix[];
  durabilityCur: number;
  durabilityMax: number;
  accountBound: boolean;
  salvageGold: number;
}

export type MerchantSalvageIneligibleReasonCode =
  | 'equipped'
  | 'listed'
  | 'not_sealed'
  | 'source'
  | 'already_salvaged'
  | 'unknown_rarity';

export interface MerchantSalvageIneligibleItem {
  itemInstanceId: string;
  rarity: Rarity;
  slotType: ItemSlotType;
  reasonCode: MerchantSalvageIneligibleReasonCode;
  reason: string;
}

export interface MerchantStatus {
  goldBalance: number;
  supplies: SupplyCounts;
  baseKit: SupplyCounts;
  extraCaps: SupplyCounts;
  totalCaps: SupplyCounts;
  /** Backwards-compatible alias for totalCaps. */
  caps: SupplyCounts;
  depthUnlocks: Record<Rarity, boolean>;
  prices: SupplyCounts;
  effects: SupplyEffects;
  repairRates: Record<Rarity, number>;
  repairableItems: MerchantRepairableItem[];
  salvageItems: MerchantSalvageItem[];
  salvageIneligibleItems: MerchantSalvageIneligibleItem[];
}

export interface MerchantBuySupplyResult {
  goldBalance: number;
  supplies: SupplyCounts;
}

export interface MerchantRepairItemResult {
  goldBalance: number;
  item: MerchantRepairableItem;
}

export interface MerchantSalvageItemResult {
  goldBalance: number;
  itemInstanceId: string;
  goldCredited: number;
}

// ── Spectate / non-holder funnel (Phase J) ────────────────────────────────────

export type SpectateOutcome = 'extracted' | 'died' | 'incomplete';

export interface SpectateRunSummary {
  id: string;
  title: string;
  subtitle: string;
  playerLabel: string;
  kind: 'demo';
  runType: 'reward' | 'echo';
  status: SpectateOutcome;
  floorsReached: number;
  turns: number;
  frameCount: number;
  bankedGold: number | null;
}

/**
 * The render-safe run snapshot inside a spectate frame. Structurally a {@link RunState}
 * minus the private `accountId`/`seed` (a sanitized `playerLabel` replaces them). Pass it
 * through {@link spectateRenderRun} to reuse the live canvas stage read-only.
 */
export interface SpectateRunState extends Omit<RunState, 'accountId' | 'seed'> {
  playerLabel: string;
}

export interface SpectateFrame {
  index: number;
  turn: number;
  floor: number;
  action: string | null;
  outcome: string;
  events: EngineEvent[];
  logLine: string;
  terminal: boolean;
  run: SpectateRunState;
}

export interface SpectateRunsResponse {
  runs: SpectateRunSummary[];
}

export interface SpectateRunResponse {
  run: SpectateRunSummary;
}

export interface SpectateEventsResponse {
  run: SpectateRunSummary;
  frames: SpectateFrame[];
}

export interface SpectateWaystationStation {
  name: string;
  status: 'open' | 'locked';
  blurb: string;
}

export interface SpectateWaystationPreview {
  title: string;
  blurb: string;
  note: string;
  stations: SpectateWaystationStation[];
}

/** Adapt a sanitized spectate run to the live `RunState` the canvas stage renders. */
export function spectateRenderRun(run: SpectateRunState): RunState {
  return { ...run, accountId: run.playerLabel, seed: '' };
}

// ── Inventory / equipment ─────────────────────────────────────────────────────

export interface ItemInstance {
  id: string;
  accountId: string | null;
  rarity: Rarity;
  slotType: ItemSlotType;
  affixes: ItemAffix[] | unknown;
  durabilityCur: number;
  durabilityMax: number;
  sealDepth: number;
  sealState: SealState;
  accountBound: boolean;
  source: string;
  sourceRunId: string | null;
  sourceTempId: string | null;
  sourceFloor: number | null;
  sealedAt: string | null;
  createdAt: string;
}

export interface InventoryItem {
  itemInstanceId: string;
  equippedSlot: EquipSlot | null;
  acquiredAt: string;
  item: ItemInstance;
}

export interface EquipmentEffects {
  maxHp: number;
  attack: number;
  defense: number;
  lanternEfficiencyBps: number;
  burdenReduction: number;
  maxLantern?: number;
  oilRestoreBonus?: number;
  torchRestoreBonus?: number;
  potionHealBonus?: number;
}

export interface EquipmentResponse {
  items: InventoryItem[];
  effects: EquipmentEffects;
}

export interface InventoryResponse {
  items: InventoryItem[];
}

// ── Waystation hub (Phase F) ──────────────────────────────────────────────────

export type HubAccess = 'full' | 'limited';

/** A publicly-shareable equipped slot summary (no item instance ids). */
export interface HubEquip {
  slot: EquipSlot;
  slotType: ItemSlotType;
  rarity: Rarity;
  title: string;
}

export interface HubPresenceEntry {
  accountId: string;
  walletLabel: string;
  equipped: HubEquip[];
  cosmetics: EquippedCosmetic[];
  lanternFlame: string;
  emote: string | null;
  /** ISO window of the active timed emote bubble (null when no emote). */
  emoteStartedAt?: string | null;
  emoteExpiresAt?: string | null;
  ageMs: number;
  x?: number;
  y?: number;
  facing?: HubFacing;
  lastSeenAt?: string;
}

export interface HubSelf {
  accountId: string;
  walletLabel: string;
  eligibilityState: EligibilityState;
  lastCheckResult: EligibilityClass | null;
}

export interface HubEnterResponse {
  access: HubAccess;
  reason: string | null;
  self: HubSelf;
  presence: HubPresenceEntry[];
}

export interface HubHeartbeatResponse {
  access: HubAccess;
  reason: string | null;
  presence: HubPresenceEntry[];
}

export interface HubHeartbeatInput {
  x?: number;
  y?: number;
  facing?: HubFacing;
  emote?: string | null;
}

export interface HubPresenceResponse {
  presence: HubPresenceEntry[];
}

export interface HubInspectResponse {
  accountId: string;
  walletLabel: string;
  status: EligibilityState;
  equipped: HubEquip[];
  cosmetics: EquippedCosmetic[];
}

// ── Cosmetics (Phase K) ─────────────────────────────────────────────────────

export interface Cosmetic {
  id: string;
  kind: CosmeticKind;
  slot: CosmeticSlot;
  title: string;
  description: string;
  price: number;
  active: boolean;
  tradeable: boolean;
  accountBound: boolean;
  metadata: unknown;
  owned: boolean;
  equipped: boolean;
  /** Copies owned (multi-copy ownership); 0 when not owned. */
  quantity: number;
  /** Copies reserved by the owner's active marketplace listings. */
  listedCount: number;
  /** Copies free to list (quantity - listedCount - 1 if equipped). */
  listableCount: number;
  listed: boolean;
  stockTotal: number | null;
  stockSold: number | null;
  stockRemaining: number | null;
  soldOut: boolean;
  acquisitionKind: string;
  acquiredAt?: string;
}

export interface EquippedCosmetic {
  id: string;
  kind: CosmeticKind;
  slot: CosmeticSlot;
  title: string;
  metadata: unknown;
}

export interface CosmeticShopResponse {
  cosmetics: Cosmetic[];
  goldBalance: number;
}

export interface MyCosmeticsResponse {
  owned: Cosmetic[];
  equipped: EquippedCosmetic[];
  goldBalance: number;
}

export interface CosmeticListing {
  id: string;
  /** Gold price for gold listings; 0 for $OBOL listings. */
  price: number;
  currency: ListingCurrency;
  obolAmountTotal: number | null;
  sellerObolAmount: number | null;
  treasuryObolAmount: number | null;
  /** Expiry of an active buyer purchase intent reserving this listing (may be past). */
  reservedUntil: string | null;
  status: ListingStatus;
  sellerAccountId: string;
  sellerLabel: string;
  buyerLabel: string | null;
  cosmeticId: string;
  cosmetic: Cosmetic;
  listedAt: string;
  closedAt: string | null;
}

export interface CosmeticListingsResponse {
  listings: CosmeticListing[];
}

export interface MyCosmeticListingsResponse {
  listings: CosmeticListing[];
  goldBalance: number;
}

export interface BuyCosmeticListingResult {
  listing: CosmeticListing;
  cosmeticId: string;
  pricePaid: number;
  fee: number;
  sellerProceeds: number;
  buyerGoldBalance: number;
}

export interface CosmeticClaimRequirementStatus {
  symbol: string;
  threshold: number;
  mint?: string;
  state: EligibilityClass;
  balance?: string;
  decimals?: number;
}

export interface CosmeticClaimCampaignStatus {
  campaignId: string;
  cosmeticId: string;
  title: string;
  description: string;
  kind: CosmeticKind;
  slot: CosmeticSlot;
  metadata: unknown;
  capTotal: number | null;
  claimedCount: number;
  remaining: number | null;
  threshold: number | null;
  requirements: CosmeticClaimRequirementStatus[];
  eligibilityState: EligibilityState | null;
  lastCheckResult: EligibilityClass | null;
  alreadyClaimed: boolean;
  walletAlreadyClaimed: boolean;
  soldOut: boolean;
  expired: boolean;
  active: boolean;
  claimStartAt: string | null;
  claimEndAt: string | null;
  canClaim: boolean;
}

export type CosmeticFounderClaimStatus = CosmeticClaimCampaignStatus & {
  capTotal: number;
  remaining: number;
  threshold: number;
  eligibilityState: EligibilityState;
};

export interface CosmeticClaimCampaignsResponse {
  campaigns: CosmeticClaimCampaignStatus[];
}

export interface CosmeticFounderClaimResponse {
  status: CosmeticFounderClaimStatus;
  collection: MyCosmeticsResponse;
}

export interface CosmeticClaimCampaignResponse {
  status: CosmeticClaimCampaignStatus;
  collection: MyCosmeticsResponse;
}

// ── Marketplace (Phase G + dual currency) ─────────────────────────────────────

export type ListingStatus = 'listed' | 'sold' | 'cancelled';
export type ListingCurrency = 'gold' | 'obol';

/** Public, non-sensitive item fields shown on a marketplace listing. */
export interface MarketListingItem {
  rarity: Rarity;
  slotType: ItemSlotType;
  affixes: ItemAffix[] | unknown;
  durabilityCur: number;
  durabilityMax: number;
  sealDepth: number;
  sealState: SealState;
  accountBound: boolean;
}

export interface MarketListing {
  id: string;
  /** Gold price for gold listings; 0 for $OBOL listings. */
  price: number;
  currency: ListingCurrency;
  obolAmountTotal: number | null;
  sellerObolAmount: number | null;
  treasuryObolAmount: number | null;
  /** Expiry of an active buyer purchase intent reserving this listing (may be past). */
  reservedUntil: string | null;
  status: ListingStatus;
  sellerAccountId: string;
  sellerLabel: string;
  buyerLabel: string | null;
  itemInstanceId: string;
  item: MarketListingItem;
  listedAt: string;
  closedAt: string | null;
}

export interface MarketListingsResponse {
  listings: MarketListing[];
}

export interface MyListingsResponse {
  listings: MarketListing[];
  goldBalance: number;
}

// ── $OBOL marketplace purchase intents ────────────────────────────────────────

export interface MarketIntentPaymentInstructions {
  mint: string;
  totalAmount: number;
  sellerAmount: number;
  treasuryAmount: number;
  sellerRecipient: string;
  /** Seller WALLET (ATA owner), when known. Never a token-account address. */
  sellerOwner?: string;
  treasuryRecipient: string;
  reference: string;
  memo: string;
  expiresAt: string;
  devMockSignature?: string;
}

export type MarketListingKind = 'gear' | 'cosmetic';
export type MarketPurchaseIntentStatus = 'active' | 'paid' | 'expired' | 'cancelled';

export interface MarketPurchaseIntent {
  id: string;
  listingKind: MarketListingKind;
  listingId: string;
  buyerAccountId: string;
  status: MarketPurchaseIntentStatus;
  paymentReference: string;
  obolAmountTotal: number;
  sellerObolAmount: number;
  treasuryObolAmount: number;
  createdAt: string;
  expiresAt: string;
  paidAt: string | null;
  txSignature: string | null;
  paymentInstructions: MarketIntentPaymentInstructions | null;
}

export interface MyMarketPurchaseIntentsResponse {
  intents: MarketPurchaseIntent[];
}

export interface MarketTokenSettlement {
  intent: MarketPurchaseIntent;
  listingKind: MarketListingKind;
  listingId: string;
  txSignature: string;
}

export interface BuyListingResult {
  listing: MarketListing;
  itemInstanceId: string;
  pricePaid: number;
  fee: number;
  sellerProceeds: number;
  buyerGoldBalance: number;
}

// ── Solana payment metadata (Phase M3) ──────────────────────────────────────

export type SpinnerBurnMode = 'true_burn' | 'burn_address';

export interface SolanaPaymentContext {
  mint: string;
  tokenProgramId: string;
  decimals: number;
  latestBlockhash: {
    blockhash: string;
    lastValidBlockHeight: number;
  };
}

/** Wallet balance for one mint (raw smallest units), used as a payment preflight. */
export interface SolanaTokenBalance {
  mint: string;
  owner: string;
  amountRaw: string;
  decimals: number | null;
}

// ── Gold cash-out / Gold Exchange (Phase H) ──────────────────────────────────

export type GoldSaleOrderStatus = 'active' | 'paid' | 'cancelled' | 'expired';

export interface CashoutPaymentInstructions {
  mint: string;
  totalAmount: number;
  sellerAmount: number;
  treasuryAmount: number;
  sellerRecipient: string;
  sellerOwner?: string;
  treasuryRecipient: string;
  reference: string;
  memo: string;
  expiresAt: string;
  devMockSignature?: string;
}

export interface GoldSaleOrder {
  id: string;
  sellerAccountId: string;
  sellerLabel: string;
  buyerLabel: string | null;
  goldAmount: number;
  obolAmountTotal: number;
  sellerObolAmount: number;
  treasuryObolAmount: number;
  status: GoldSaleOrderStatus;
  expectedMint: string;
  createdAt: string;
  expiresAt: string;
  paidAt: string | null;
  cancelledAt: string | null;
  txSignature: string | null;
  /** Active buyer-reservation end, when another buyer holds the order. */
  reservedUntil: string | null;
}

export interface CashoutOrdersResponse {
  orders: GoldSaleOrder[];
}

export interface MyCashoutOrdersResponse {
  orders: GoldSaleOrder[];
  goldBalance: number;
}

// ── Gold Exchange buyer purchase intents (audit1 PAY-1/PAY-2) ────────────────

export type GoldSaleIntentStatus = 'active' | 'paid' | 'expired' | 'cancelled';

export interface GoldSalePurchaseIntent {
  id: string;
  goldSaleOrderId: string;
  buyerAccountId: string;
  status: GoldSaleIntentStatus;
  paymentReference: string;
  goldAmount: number;
  obolAmountTotal: number;
  sellerObolAmount: number;
  treasuryObolAmount: number;
  createdAt: string;
  expiresAt: string;
  paidAt: string | null;
  txSignature: string | null;
  paymentInstructions: CashoutPaymentInstructions | null;
}

export interface MyGoldSalePurchaseIntentsResponse {
  intents: GoldSalePurchaseIntent[];
}

export interface SubmitCashoutTxResult {
  order: GoldSaleOrder;
  intent?: GoldSalePurchaseIntent;
  goldCredited: number;
  buyerGoldBalance: number;
  txSignature: string;
}

// ── Spinner / Ferryman's Wager (Phase I) ─────────────────────────────────────

export type SpinnerOrderStatus = 'pending' | 'paid' | 'cancelled' | 'expired' | 'settled';

export interface SpinnerPaymentInstructions {
  mint: string;
  totalAmount: number;
  burnAmount: number;
  treasuryAmount: number;
  burnRecipient: string;
  burnMode: SpinnerBurnMode;
  treasuryRecipient: string;
  reference: string;
  memo: string;
  expiresAt: string;
  devMockSignature?: string;
}

export interface SpinnerOrder {
  id: string;
  accountId: string;
  status: SpinnerOrderStatus;
  priceTotal: number;
  burnAmount: number;
  treasuryAmount: number;
  /** USD quote snapshot stored on the order (null on legacy fixed-price orders). */
  targetUsdCents: number | null;
  tokenPriceUsd: string | null;
  tokenDecimals: number | null;
  priceSource: string | null;
  priceObservedAt: string | null;
  expectedMint: string;
  expectedBurnRecipient: string;
  expectedTreasuryRecipient: string;
  paymentReference: string;
  rewardTableVersion: number;
  createdAt: string;
  expiresAt: string;
  paidAt: string | null;
  settledAt: string | null;
  cancelledAt: string | null;
  txSignature: string | null;
  paymentInstructions: SpinnerPaymentInstructions | null;
}

export interface SpinRewardSupplies {
  oil: number;
  torch: number;
  potion: number;
}

export interface SpinRewardCosmetic {
  cosmeticId: string;
  title: string;
  campaignId: string;
}

export interface SpinReward {
  /** 'supplies' | 'cosmetic' for new spins; legacy history rows may say 'gold'/'item'. */
  type: 'supplies' | 'cosmetic' | string;
  key: string;
  rewardTableVersion: number;
  supplies: SpinRewardSupplies | null;
  /** Account supply stock after crediting a supplies reward. */
  suppliesAfter: SpinRewardSupplies | null;
  cosmetic: SpinRewardCosmetic | null;
}

export interface SpinResult {
  spinId: string;
  spinType: 'free' | 'paid';
  reward: SpinReward;
  goldBalance: number;
  nextFreeSpinAt: string | null;
}

export interface LastSpinSummary {
  spinId: string;
  spinType: 'free' | 'paid';
  orderId: string | null;
  reward: SpinReward;
  createdAt: string;
}

export interface SubmitSpinResult {
  order: SpinnerOrder;
  reward: SpinReward;
  goldBalance: number;
  txSignature: string;
}

export interface RewardOdds {
  key: string;
  type: 'supplies' | 'cosmetic';
  weight: number;
  oddsPct: number;
  detail: string;
}

export interface ActiveWeeklyCosmetic {
  campaignId: string;
  cosmeticId: string;
  title: string;
  metadata: unknown;
  startsAt: string;
  endsAt: string;
}

export interface SpinnerStatus {
  freeSpin: {
    available: boolean;
    nextFreeSpinAt: string | null;
    cooldownHours: number;
    /** Hold-to-play gate: free spins require active $OBOL eligibility (optional for older backends). */
    eligible?: boolean;
    eligibilityState?: EligibilityState;
  };
  paid: {
    /** USD quote target for one paid spin (500 = $5). */
    targetUsdCents: number;
    /** Seconds a created quote/order stays payable. */
    quoteTtlSeconds: number;
    /** Indicative current quote in smallest units (each order stores its own). */
    estimatedTotalAmount: number | null;
    tokenDecimals: number | null;
    tokenPriceUsd: string | null;
    mint: string | null;
    burnMode: SpinnerBurnMode | null;
    treasuryRecipient: string | null;
    splitBps: { burn: number; treasury: number };
    available: boolean;
    unavailableReason: string | null;
  };
  weeklyCosmetic: ActiveWeeklyCosmetic | null;
  rewardTableVersion: number;
  freeRewardTable: RewardOdds[];
  paidRewardTable: RewardOdds[];
  pendingOrders: SpinnerOrder[];
  lastSpin: LastSpinSummary | null;
  goldBalance: number;
}
